import { css } from "@emotion/react";
import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import {
  Alert,
  Button,
  Flex,
  Form,
  Icon,
  Icons,
  Input,
  Label,
  Link,
  TextField,
  View,
} from "@phoenix/components";
import { getReturnUrl, prependBasename } from "@phoenix/utils/routingUtils";

type LoginFormParams = {
  email: string;
  password: string;
};

type LoginFormProps = {
  initialError: string | null;
  /**
   * Callback function called when the form is submitted
   */
  onSubmit?: () => void;
};
export function LoginForm(props: LoginFormProps) {
  const navigate = useNavigate();
  const { initialError, onSubmit: propsOnSubmit } = props;
  const [error, setError] = useState<string | null>(initialError);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const onSubmit = useCallback(
    async (params: LoginFormParams) => {
      propsOnSubmit?.();
      setError(null);
      setIsLoading(true);

      // Sanitize email by trimming whitespace and converting to lowercase
      const sanitizedParams = {
        ...params,
        email: params.email.trim().toLowerCase(),
      };

      try {
        const response = await fetch(prependBasename("/auth/login"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(sanitizedParams),
        });
        if (!response.ok) {
          const errorMessage =
            response.status === 429
              ? "Too many requests. Please try again later."
              : "Invalid login";
          setError(errorMessage);
          return;
        }
      } catch (_error) {
        setError("Invalid login");
        return;
      } finally {
        setIsLoading(() => false);
      }
      const returnUrl = getReturnUrl();
      navigate(returnUrl);
    },
    [navigate, propsOnSubmit, setError]
  );
  const { control, handleSubmit } = useForm<LoginFormParams>({
    defaultValues: { email: "", password: "" },
  });
  return (
    <>
      {error ? (
        <View paddingBottom="size-100">
          <Alert variant="danger">{error}</Alert>{" "}
        </View>
      ) : null}
      <Form
        onSubmit={handleSubmit(onSubmit)}
        css={css`
            label, .ac-label {
                color: #0d2e38 !important; /* Deep teal/charcoal */
                font-size: 14px !important;
                margin-bottom: 8px !important;
                font-weight: 700 !important;
                letter-spacing: 0.5px !important;
            }
            input {
                background: rgba(255, 255, 255, 0.4) !important;
                border: 1px solid rgba(13, 46, 56, 0.2) !important;
                color: #0d2e38 !important;
                padding: 12px 16px !important;
                border-radius: 12px !important;
                transition: all 0.3s ease !important;
                &::placeholder {
                    color: rgba(13, 46, 56, 0.4) !important;
                }
                &:focus {
                    background: rgba(255, 255, 255, 0.6) !important;
                    border-color: #14BAB6 !important;
                    box-shadow: 0 0 0 4px rgba(20, 186, 182, 0.1) !important;
                    outline: none !important;
                }
            }
            button[data-variant="primary"] {
                background: linear-gradient(135deg, #14BAB6 0%, #00ADEE 100%) !important;
                color: #FFFFFF !important;
                border: none !important;
                padding: 15px !important;
                border-radius: 12px !important;
                font-weight: 800 !important;
                letter-spacing: 1.5px !important;
                margin-top: 10px !important;
                box-shadow: 0 10px 25px rgba(20, 186, 182, 0.3) !important;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
                &:hover:not([data-disabled]) {
                    transform: translateY(-3px) !important;
                    box-shadow: 0 15px 35px rgba(20, 186, 182, 0.4) !important;
                    filter: brightness(1.05) !important;
                }
                &:active:not([data-disabled]) {
                    transform: translateY(0) !important;
                }
                &[data-disabled] {
                    opacity: 0.5 !important;
                    cursor: not-allowed !important;
                    transform: none !important;
                    box-shadow: none !important;
                }
            }
        `}
      >
        <Flex direction="column" gap="size-200">
           <Controller
              name="email"
              control={control}
              render={({ field: { onChange, value, onBlur } }) => (
                <TextField
                  name="email"
                  isRequired
                  type="text"
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  autoComplete="email"
                  onKeyDown={(e) => {
                    // Prevent form submission on Enter - user likely wants to tab to password
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                >
                  <Label>Email</Label>
                  <Input placeholder="your email address" />
                </TextField>
              )}
            />
            <div
                css={css`
                position: relative;
                .link-container {
                    position: absolute;
                    float: right;
                    right: 0;
                    top: 5px;
                    font-size: 11px;
                    color: #0d2e38;
                    text-decoration: none;
                    font-weight: 700;
                    opacity: 0.7;
                    z-index: 10;
                    &:hover {
                        color: #14BAB6;
                        opacity: 1;
                        text-decoration: underline;
                    }
                }
                `}
            >
                <Controller
                    name="password"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                    <TextField
                        name="password"
                        type="password"
                        isRequired
                        onChange={onChange}
                        value={value}
                        autoComplete="current-password"
                    >
                        <Label>Password</Label>
                        <Input placeholder="your password" />
                    </TextField>
                    )}
                />
                <div className="link-container">
                    <Link id="forgot-password-link" to="/forgot-password">
                        Forgot your password?
                    </Link>
                </div>
            </div>
          <Button
            variant="primary"
            type="submit"
            isDisabled={isLoading}
            leadingVisual={
              isLoading ? <Icon svg={<Icons.LoadingOutline />} /> : undefined
            }
          >
            {isLoading ? "AUTHENTICATING..." : "LOG IN"}
          </Button>
        </Flex>
      </Form>
    </>
  );
}
